import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Ride } from '../../types';
import WaiverModal from '../../components/rides/WaiverModal';

interface RideDetails extends Ride {
  isParticipant: boolean;
  checkedIn?: boolean;
  isLiked?: boolean;
  likesCount?: number;
  comments?: Array<{
    id: string;
    content: string;
    createdAt: string;
    user: {
      id: string;
      firstName: string;
      lastName: string;
      avatarUrl?: string;
    };
  }>;
}

const RideDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { rideId } = useParams<{ rideId: string }>();
  const navigate = useNavigate();
  const [ride, setRide] = useState<RideDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueDescription, setIssueDescription] = useState('');
  const [submittingIssue, setSubmittingIssue] = useState(false);
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [joiningRide, setJoiningRide] = useState(false);

  useEffect(() => {
    if (rideId) {
      fetchRideDetails();
    }
  }, [rideId]);

  const fetchRideDetails = async () => {
    try {
      const response = await api.get(`/rides/${rideId}`);
      setRide(response.data);
    } catch (error) {
      console.error('Error fetching ride details:', error);
      toast.error('Failed to load ride details');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!rideId || !user) return;
    try {
      // Check-in is done by admin/community manager for a user
      // For self-check-in, we need to check if there's a self-check-in endpoint
      // For now, we'll use the admin endpoint with current user's ID
      await api.post(`/admin/rides/${rideId}/check-in/${user.id}`);
      toast.success(t('rides.checkInSuccess'));
      fetchRideDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('rides.checkInError'));
    }
  };

  const handleFinishRide = async () => {
    if (!rideId) return;
    if (!window.confirm(t('rides.finishRideConfirm'))) {
      return;
    }
    try {
      await api.post(`/admin/rides/${rideId}/finish`);
      toast.success(t('rides.finishRideSuccess'));
      fetchRideDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('rides.finishRideError'));
    }
  };

  const handleReportIssue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rideId || !issueDescription.trim()) return;

    setSubmittingIssue(true);
    try {
      await api.post('/admin/issues', {
        rideId,
        description: issueDescription.trim(),
      });
      toast.success(t('rides.issueReportedSuccess'));
      setShowIssueModal(false);
      setIssueDescription('');
      fetchRideDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('rides.issueReportedError'));
    } finally {
      setSubmittingIssue(false);
    }
  };

  const handleShareRide = async () => {
    if (!ride) return;
    // Use share-ride page as intermediate step
    // This allows users to see ride details and login/signup if needed
    // Use VITE_WEB_URL env var if set, otherwise use current origin, fallback to rider.ride-connect.app
    const webUrl = import.meta.env.VITE_WEB_URL || window.location.origin || 'https://rider.ride-connect.app';
    const shareUrl = `${webUrl}/share/ride?id=${ride.id}`;
    
    // For web platform, always copy just the URL to clipboard
    // This ensures users get a clean URL they can paste anywhere
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t('rides.shareCopied'));
    } catch (error) {
      // Fallback: use textarea method for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          toast.success(t('rides.shareCopied'));
        } else {
          // Last resort: show URL in a prompt
          const userInput = prompt(t('rides.shareText'), shareUrl);
          if (userInput) {
            toast.success(t('rides.shareCopied'));
          }
        }
      } catch (fallbackError) {
        document.body.removeChild(textArea);
        // Last resort: show URL in a prompt
        const userInput = prompt(t('rides.shareText'), shareUrl);
        if (userInput) {
          toast.success(t('rides.shareCopied'));
        }
      }
    }
  };

  const handleJoinClick = () => {
    setShowWaiverModal(true);
  };

  const handleWaiverAccept = async () => {
    if (!rideId) return;
    setJoiningRide(true);
    try {
      await api.post(`/rides/${rideId}/join`, {
        waiverAcceptedAt: new Date().toISOString(),
      });
      toast.success('Joined ride successfully');
      setShowWaiverModal(false);
      fetchRideDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('rides.joinError'));
    } finally {
      setJoiningRide(false);
    }
  };

  const handleLeave = async () => {
    if (!rideId) return;
    try {
      await api.delete(`/rides/${rideId}/leave`);
      toast.success('Left ride successfully');
      fetchRideDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('rides.leaveError'));
    }
  };

  const handleLike = async () => {
    if (!rideId) return;
    try {
      await api.post(`/social/rides/${rideId}/like`);
      fetchRideDetails();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rideId || !comment.trim()) return;

    setSubmitting(true);
    try {
      await api.post(`/social/rides/${rideId}/comments`, { content: comment });
      setComment('');
      toast.success('Comment added');
      fetchRideDetails();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">{t('common.loading')}</div>
      </div>
    );
  }

  if (!ride) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <p className="text-center text-gray-500">Ride not found</p>
      </div>
    );
  }

  // Fix timezone issue: combine date and time in local timezone
  // departureDate from API is an ISO string like "2025-12-24T00:00:00.000Z" or "2025-12-24"
  // departureTime is like "07:00"
  // Extract date part and combine with time: "2025-12-24T07:00" which will be interpreted in local timezone
  if (!ride.departureDate) {
    throw new Error('Ride missing departure date');
  }
  
  const timeStr = ride.departureTime || '00:00';
  // Extract date part from ISO string (handles both "2025-12-24T00:00:00.000Z" and "2025-12-24")
  const dateStr = String(ride.departureDate).split('T')[0]; // Get "YYYY-MM-DD" from ISO string
  const dateTimeString = `${dateStr}T${timeStr}`;
  const rideDate = new Date(dateTimeString);
  
  if (isNaN(rideDate.getTime())) {
    throw new Error(`Invalid date for ride: ${dateTimeString}`);
  }

  const isPast = rideDate < new Date();
  const isFull = (ride.participantCount || 0) >= (ride.maxRiders || 0);
  const canJoin = !ride.isParticipant && !isFull && !ride.isCompleted && !isPast;
  
  // Check if user can check in (is participant, not checked in, ride not completed, not past)
  const canCheckIn = ride.isParticipant && !ride.checkedIn && !ride.isCompleted && !isPast;
  
  // Check if user can finish ride (is community manager+ and ride not completed and is past)
  const canFinishRide = user && (
    user.role === 'COMMUNITY_MANAGER' ||
    user.role === 'STORE_MANAGER' ||
    user.role === 'STORE_ADMIN' ||
    user.role === 'ADMIN' ||
    user.role === 'SYSTEM_ADMIN' ||
    user.role === 'SUPERADMIN'
  ) && !ride.isCompleted && isPast;

  // Check if user can report issue (is admin+)
  const canReportIssue = user && (
    user.role === 'ADMIN' ||
    user.role === 'SYSTEM_ADMIN' ||
    user.role === 'SUPERADMIN'
  );

  // Check if user can edit this ride
  const canEditRide = user && (
    // User is the creator
    (ride.createdBy === user.id) ||
    // User is STORE_ADMIN/STORE_MANAGER and ride belongs to their store
    ((user.role === 'STORE_ADMIN' || user.role === 'STORE_MANAGER') && ride.storeId === user.storeId) ||
    // User is CLUB_ADMIN and ride belongs to their club
    (user.role === 'CLUB_ADMIN' && ride.clubId === user.clubId) ||
    // User is SYSTEM_ADMIN or SUPERADMIN
    (user.role === 'SYSTEM_ADMIN' || user.role === 'SUPERADMIN')
  );

  const handleEditRide = () => {
    if (!rideId) return;
    navigate(`/rides/edit/${rideId}`);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/rides')}
        className="mb-4 text-primary-600 hover:text-primary-700"
      >
        ← {t('common.back')}
      </button>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <h1 className="text-3xl font-bold text-gray-900">{ride.name}</h1>
          <div className="flex space-x-2">
            {ride.isParticipant && (
              <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                {t('rides.joined')}
              </span>
            )}
            {ride.isCompleted && (
              <span className="px-3 py-1 bg-primary-100 text-primary-800 text-sm font-medium rounded-full">
                {t('rides.completed')}
              </span>
            )}
            {isPast && !ride.isCompleted && (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                {t('rides.past')}
              </span>
            )}
          </div>
        </div>
        <p className="text-gray-700 mb-6 whitespace-pre-line">{ride.description}</p>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <span className="text-sm font-medium text-gray-500">{t('rides.startLocation')}</span>
            <p className="text-gray-900">{ride.departureLocation}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500">{t('rides.startTime')}</span>
            <p className="text-gray-900">{format(rideDate, 'MMM d, yyyy h:mm a')}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500">{t('rides.distance')}</span>
            <p className="text-gray-900">{ride.distanceKm} km</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500">{t('rides.participants')}</span>
            <p className="text-gray-900">
              {ride.participantCount || ride.participants?.length || 0} / {ride.maxRiders}
            </p>
            {isFull && (
              <p className="text-sm text-red-600 mt-1">{t('rides.rideFull')}</p>
            )}
          </div>
          {ride.elevationGainM && (
            <div>
              <span className="text-sm font-medium text-gray-500">{t('rides.elevationGain')}</span>
              <p className="text-gray-900">{ride.elevationGainM} m</p>
            </div>
          )}
          {ride.estimatedDurationMinutes && (
            <div>
              <span className="text-sm font-medium text-gray-500">{t('rides.estimatedDuration')}</span>
              <p className="text-gray-900">{ride.estimatedDurationMinutes} {t('rides.minutes')}</p>
            </div>
          )}
          {ride.cost && ride.cost > 0 && (
            <div>
              <span className="text-sm font-medium text-gray-500">{t('rides.cost')}</span>
              <p className="text-gray-900">${ride.cost.toFixed(2)}</p>
            </div>
          )}
          {ride.loyaltyPoints && ride.loyaltyPoints > 0 && (
            <div>
              <span className="text-sm font-medium text-gray-500">{t('rides.loyaltyPoints')}</span>
              <p className="text-gray-900">{ride.loyaltyPoints} {t('rides.points')}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-4">
          {canEditRide && (
            <button
              onClick={handleEditRide}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {t('rides.editRide') || 'Edit Ride'}
            </button>
          )}
          
          {ride.isParticipant ? (
            <>
              {canCheckIn && (
                <button
                  onClick={handleCheckIn}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {t('rides.checkIn')}
                </button>
              )}
              {ride.checkedIn && (
                <span className="px-4 py-2 bg-green-100 text-green-800 rounded-md">
                  {t('rides.checkedIn')}
                </span>
              )}
              <button
                onClick={handleLeave}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                {t('rides.leave')}
              </button>
            </>
          ) : (
            <button
              onClick={handleJoinClick}
              disabled={!canJoin}
              className={`px-4 py-2 rounded-md ${
                canJoin
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isFull ? t('rides.rideFull') : ride.isCompleted ? t('rides.completed') : isPast ? t('rides.past') : t('rides.join')}
            </button>
          )}
          
          {canFinishRide && (
            <button
              onClick={handleFinishRide}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              {t('rides.finishRide')}
            </button>
          )}

          {canReportIssue && (
            <button
              onClick={() => setShowIssueModal(true)}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
            >
              {t('rides.reportIssue')}
            </button>
          )}

          <button
            onClick={handleShareRide}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            {t('rides.share')}
          </button>

          <button
            onClick={handleLike}
            className={`px-4 py-2 rounded-md ${
              ride.isLiked
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {ride.isLiked ? t('rides.liked') : t('rides.like')} ({ride.likesCount || 0})
          </button>
        </div>

        {/* Route Links */}
        {(ride.stravaUrl || ride.gpxFileUrl) && (
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">{t('rides.route')}</h3>
            <div className="flex space-x-3">
              {ride.stravaUrl && (
                <a
                  href={ride.stravaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 border border-orange-500 text-orange-600 rounded-md hover:bg-orange-50"
                >
                  {t('rides.viewStrava')}
                </a>
              )}
              {ride.gpxFileUrl && (
                <a
                  href={ride.gpxFileUrl}
                  download
                  className="px-4 py-2 border border-primary-600 text-primary-600 rounded-md hover:bg-primary-50"
                >
                  {t('rides.downloadGPX')}
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {ride.participants && ride.participants.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {t('rides.participants')} ({ride.participantCount || ride.participants.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ride.participants.map((participant) => (
              <div key={participant.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                <div className="flex items-center space-x-3">
                  {participant.user.avatarUrl ? (
                    <img
                      src={participant.user.avatarUrl}
                      alt={`${participant.user.firstName} ${participant.user.lastName}`}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-600 font-medium">
                        {participant.user.firstName?.[0]}{participant.user.lastName?.[0]}
                      </span>
                    </div>
                  )}
                  <span className="text-gray-900">
                    {participant.user.firstName} {participant.user.lastName}
                  </span>
                </div>
                {participant.checkedIn && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                    {t('rides.checkedIn')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">{t('rides.comments')}</h2>
        <form onSubmit={handleAddComment} className="mb-4">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('rides.addComment')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2"
            rows={3}
          />
          <button
            type="submit"
            disabled={submitting || !comment.trim()}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {t('rides.comment')}
          </button>
        </form>
        <div className="space-y-4">
          {ride.comments && ride.comments.length > 0 ? ride.comments.map((comment) => (
            <div key={comment.id} className="border-b border-gray-200 pb-4">
              <div className="flex justify-between mb-1">
                <span className="font-medium">
                  {comment.user.firstName} {comment.user.lastName}
                </span>
                <span className="text-sm text-gray-500">
                  {format(new Date(comment.createdAt), 'MMM d, h:mm a')}
                </span>
              </div>
              <p className="text-gray-700">{comment.content}</p>
            </div>
          )) : (
            <p className="text-gray-500 text-center py-4">{t('rides.noComments')}</p>
          )}
        </div>
      </div>

      {/* Issue Report Modal */}
      {showIssueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{t('rides.reportIssue')}</h2>
            <form onSubmit={handleReportIssue}>
              <textarea
                value={issueDescription}
                onChange={(e) => setIssueDescription(e.target.value)}
                placeholder={t('rides.issueDescriptionPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
                rows={5}
                required
              />
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowIssueModal(false);
                    setIssueDescription('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={submittingIssue || !issueDescription.trim()}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
                >
                  {submittingIssue ? t('common.loading') : t('rides.submitIssue')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Waiver Modal */}
      <WaiverModal
        isOpen={showWaiverModal}
        onClose={() => setShowWaiverModal(false)}
        onAccept={handleWaiverAccept}
        organizerName={ride.store?.name || 'RideConnect'}
        isLoading={joiningRide}
      />
    </div>
  );
};

export default RideDetailsPage;
